import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import TaskCard from "@/components/TaskCard";
import { ExecutionScoreCard, GRADE_DOT, GRADE_TEXT } from "@/components/ExecutionScore";
import { LeadershipScoreCard, LeadershipRankList } from "@/components/LeadershipScore";
import { formatWIB } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ClipboardList, AlertTriangle, CheckCircle2, Users, TrendingUp, GanttChartSquare, Lightbulb, ArrowRight,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, tone }) {
  const tones = {
    indigo: "from-indigo-500 to-purple-500", rose: "from-rose-500 to-orange-500",
    sky: "from-sky-500 to-cyan-500", emerald: "from-emerald-500 to-teal-500", amber: "from-amber-500 to-orange-500",
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm">
      <div className={cn("inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br text-white mb-3", tones[tone])}><Icon className="h-5 w-5" /></div>
      <p className="text-2xl font-extrabold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children, count }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-slate-400" />
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{title}</h3>
        {count != null && <span className="text-xs font-bold text-slate-400">({count})</span>}
      </div>
      {children}
    </div>
  );
}

const wlColor = { rendah: "text-slate-500", optimal: "text-emerald-600", tinggi: "text-amber-600", kelebihan: "text-rose-600" };

export function TeamDashboard({ onOpen }) {
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [leadership, setLeadership] = useState(null);

  useEffect(() => {
    api.get("/dashboard/team").then((r) => setData(r.data));
    api.get("/metrics/execution", { params: { scope: "team" } }).then((r) => setMetrics(r.data));
    api.get("/metrics/timeline").then((r) => setTimeline(r.data));
    api.get("/metrics/suggestions").then((r) => setSuggestions(r.data));
    api.get("/metrics/leadership").then((r) => setLeadership(r.data));
  }, []);

  if (!data) return <div className="text-slate-400 py-10 text-center">Memuat...</div>;
  const s = data.summary;
  const scoreByUser = {};
  (metrics?.members || []).forEach((m) => { scoreByUser[m.user_id] = m; });

  return (
    <div className="space-y-8 zoa-fade-up">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={ClipboardList} label="Tugas Aktif Tim" value={s.active} tone="indigo" />
        <StatCard icon={AlertTriangle} label="Terlambat" value={s.overdue} tone="rose" />
        <StatCard icon={AlertTriangle} label="Terkendala" value={s.blocked} tone="amber" />
        <StatCard icon={CheckCircle2} label="Menunggu Verifikasi Saya" value={s.waiting_verification} tone="sky" />
        <StatCard icon={Users} label="Anggota Tim" value={s.members} tone="emerald" />
      </div>

      {metrics && (
        <div className="grid lg:grid-cols-3 gap-4">
          <ExecutionScoreCard data={metrics.overall} title="Skor Eksekusi Tim" />
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Skor per Anggota (terendah dulu)</p>
            <div className="space-y-2">
              {(metrics.members || []).slice(0, 6).map((m) => (
                <div key={m.user_id} className="flex items-center gap-3" data-testid={`member-score-${m.user_id}`}>
                  <span className="text-sm text-slate-700 flex-1 truncate">{m.name}</span>
                  <span className="text-xs text-slate-400">{m.counts.overdue_now} terlambat · {m.counts.revisions} revisi</span>
                  <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden">
                    <div className={cn("h-full rounded-full", GRADE_DOT[m.grade.color])} style={{ width: `${m.score}%` }} />
                  </div>
                  <span className={cn("text-sm font-bold w-8 text-right", GRADE_TEXT[m.grade.color])}>{m.score}</span>
                </div>
              ))}
              {(metrics.members || []).length === 0 && <p className="text-sm text-slate-400 italic">Belum ada data anggota.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Leadership score */}
      {leadership && (leadership.me || leadership.leaders?.length > 0) && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="leadership-section">
          {leadership.me && <LeadershipScoreCard data={leadership.me} title="Skor Kepemimpinan Saya" />}
          <div className={leadership.me ? "lg:col-span-2" : "lg:col-span-3"}>
            <LeadershipRankList leaders={leadership.leaders} meId={leadership.me?.user_id} title="Skor Kepemimpinan Atasan" />
          </div>
        </div>
      )}

      {/* Reallocation suggestions */}
      {suggestions?.suggestions?.length > 0 && (
        <Section title="Saran Realokasi Beban" icon={Lightbulb} count={suggestions.suggestions.length}>
          <div className="grid sm:grid-cols-2 gap-3">
            {suggestions.suggestions.map((sg, i) => (
              <div key={i} className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4" data-testid={`suggestion-${i}`}>
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-semibold text-rose-600">{sg.from_name} ({sg.from_util}%)</span>
                  {sg.to_name && <><ArrowRight className="h-4 w-4 text-slate-400" /><span className="font-semibold text-emerald-600">{sg.to_name} ({sg.to_util}%)</span></>}
                </div>
                <p className="text-xs text-slate-500 mt-1.5">{sg.reason}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Workload table with score */}
      <Section title="Beban Kerja Tim" icon={TrendingUp}>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-2.5">Anggota</th><th className="text-center px-3">Aktif</th><th className="text-center px-3">Selesai</th><th className="text-center px-3">Terlambat</th><th className="text-center px-3">Est. Jam</th><th className="text-center px-3">Skor</th><th className="text-right px-4">Utilisasi</th></tr>
            </thead>
            <tbody>
              {data.members.map((m) => {
                const sc = scoreByUser[m.user?.id];
                return (
                  <tr key={m.user?.id} className="border-t border-slate-100" data-testid={`team-member-${m.user?.id}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{m.user?.name}</td>
                    <td className="text-center">{m.active}</td>
                    <td className="text-center text-emerald-600">{m.done}</td>
                    <td className="text-center text-rose-600">{m.overdue}</td>
                    <td className="text-center">{m.est_hours}j</td>
                    <td className={cn("text-center font-bold", sc ? GRADE_TEXT[sc.grade.color] : "text-slate-300")}>{sc ? sc.score : "–"}</td>
                    <td className={cn("text-right px-4 font-bold", wlColor[m.level])}>{m.utilization}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Timeline / Gantt */}
      {timeline?.rows?.length > 0 && <TimelineGantt rows={timeline.rows} onOpen={onOpen} />}

      {data.overdue_tasks.length > 0 && (
        <Section title="Tugas Tim Terlambat" icon={AlertTriangle} count={data.overdue_tasks.length}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.overdue_tasks.map((t) => <TaskCard key={t.id} task={t} onClick={() => onOpen(t.id)} />)}</div>
        </Section>
      )}
      {data.blocked_tasks.length > 0 && (
        <Section title="Tugas Terkendala" icon={AlertTriangle} count={data.blocked_tasks.length}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.blocked_tasks.map((t) => <TaskCard key={t.id} task={t} onClick={() => onOpen(t.id)} />)}</div>
        </Section>
      )}
    </div>
  );
}

function TimelineGantt({ rows, onOpen }) {
  const times = rows.flatMap((r) => [r.start, r.deadline].filter(Boolean)).map((d) => new Date(d).getTime());
  const min = Math.min(...times), max = Math.max(...times);
  const span = Math.max(max - min, 1);
  const now = Date.now();
  const nowPct = Math.max(0, Math.min(100, ((now - min) / span) * 100));
  const prColor = { kritis: "from-rose-500 to-red-500", tinggi: "from-amber-500 to-orange-500", normal: "from-indigo-500 to-purple-500", rendah: "from-slate-400 to-slate-500" };
  return (
    <Section title="Timeline Tenggat Tim" icon={GanttChartSquare} count={rows.length}>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 relative" data-testid="team-timeline">
        <div className="absolute top-4 bottom-4 w-px bg-rose-400/60 z-10" style={{ left: `calc(30% + ${nowPct * 0.7}%)` }} title="Sekarang" />
        <div className="space-y-2">
          {rows.map((r) => {
            const st = r.start ? new Date(r.start).getTime() : min;
            const en = new Date(r.deadline).getTime();
            const left = ((st - min) / span) * 100;
            const width = Math.max(((en - st) / span) * 100, 2);
            return (
              <div key={r.id} className="flex items-center gap-2 text-xs">
                <button onClick={() => onOpen(r.id)} className="w-[30%] truncate text-left text-slate-600 hover:text-indigo-600 font-medium" data-testid={`timeline-row-${r.id}`}>{r.title}</button>
                <div className="flex-1 relative h-5 bg-slate-50 rounded">
                  <div className={cn("absolute h-5 rounded bg-gradient-to-r flex items-center px-1.5 overflow-hidden", prColor[r.priority] || prColor.normal, r.is_overdue && "ring-2 ring-rose-400")}
                    style={{ left: `${left * 0.7}%`, width: `${width * 0.7}%`, minWidth: "8px" }}>
                    <span className="text-[9px] text-white/90 truncate">{r.assignee || ""}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
